"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useRouter, useParams } from "next/navigation"
import { HttpTypes } from "@medusajs/types"
import { searchProducts } from "@lib/data/products"
import Search from "@modules/common/icons/search"
import Thumbnail from "@modules/products/components/thumbnail"
import { convertToLocale } from "@lib/util/money"

type SearchBoxProps = {
  className?: string
}

const SearchBox = ({ className }: SearchBoxProps) => {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<HttpTypes.StoreProduct[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const { countryCode } = useParams() as { countryCode: string }

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.length >= 2) {
        setIsLoading(true)
        try {
          const products = await searchProducts({
            query,
            countryCode,
            limit: 6,
          })
          setResults(products)
          setIsOpen(true)
        } catch (error) {
          console.error("Search error:", error)
          setResults([])
        } finally {
          setIsLoading(false)
        }
      } else {
        setResults([])
        setIsOpen(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [query, countryCode])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      router.push(`/${countryCode}/search?q=${encodeURIComponent(query.trim())}`)
      setIsOpen(false)
      setQuery("")
    }
  }

  const handleProductClick = (handle: string) => {
    router.push(`/${countryCode}/products/${handle}`)
    setIsOpen(false)
    setQuery("")
  }

  const getProductPrice = (product: HttpTypes.StoreProduct) => {
    const variant = product.variants?.[0]
    if (variant?.calculated_price) {
      return convertToLocale({
        amount: variant.calculated_price.calculated_amount || 0,
        currency_code: variant.calculated_price.currency_code || "USD",
      })
    }
    return null
  }

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit}>
        <div className="flex items-center bg-beige-light rounded-full px-4 py-2">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
            placeholder="Search..."
            className="bg-transparent text-sm text-brown-dark placeholder-brown-light outline-none w-32 lg:w-40 font-sans"
          />
          <button type="submit" className="text-brown-light hover:text-brown-dark transition-colors ml-2">
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-brown-light border-t-transparent rounded-full animate-spin" />
            ) : (
              <Search size="16" color="currentColor" />
            )}
          </button>
        </div>
      </form>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-cream border border-beige rounded-lg shadow-lg overflow-hidden z-50 min-w-[320px] -translate-x-1/4 lg:translate-x-0">
          {results.length > 0 ? (
            <>
              <div className="max-h-[400px] overflow-y-auto">
                {results.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product.handle!)}
                    className="w-full flex items-center gap-4 p-3 hover:bg-beige-light transition-colors text-left border-b border-beige last:border-b-0"
                  >
                    <div className="w-14 h-14 rounded-md overflow-hidden bg-beige-light flex-shrink-0">
                      <Thumbnail
                        thumbnail={product.thumbnail}
                        images={product.images}
                        size="square"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-brown-dark truncate">
                        {product.title}
                      </p>
                      <p className="text-xs text-brown-light truncate">
                        {product.collection?.title || "Bittoo Jewellers Collection"}
                      </p>
                      {getProductPrice(product) && (
                        <p className="text-sm font-semibold text-gold mt-1">
                          {getProductPrice(product)}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={handleSubmit}
                className="w-full p-3 text-sm font-medium text-gold hover:bg-beige-light transition-colors border-t border-beige"
              >
                View all results for "{query}"
              </button>
            </>
          ) : query.length >= 2 && !isLoading ? (
            <div className="p-6 text-center">
              <p className="text-brown-light text-sm">No products found for "{query}"</p>
              <p className="text-brown-light/70 text-xs mt-1">Try a different search term</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default SearchBox

